/**
 * Tela do laudo da modalidade assinatura eletrônica (E74.F3, ADR-0075 §7).
 *
 * A etapa "Laudo" do caso eletrônico. Não há narrativa a redigir nem critérios
 * a revisar: o documento sai pronto do que o motor apurou e do que o perito
 * concluiu em cada CCB na mesa. Esta tela existe para o perito **conferir o que
 * vai sair** antes de baixar — a mesma razão do preview do laudo grafotécnico.
 *
 * Montada pelo `LaudoCasoPage` quando `case.modalidade === 'eletronica'`; a
 * página recebe o caso já carregado e não o busca de novo.
 *
 * Quando o laudo ainda não pode ser emitido, a tela **nomeia o que falta**: o
 * servidor devolve as CCBs por concluir, e mandar o perito de volta à mesa com
 * a informação na mão é o que separa isto de um botão que não funciona.
 *
 * F-E162.F2-02 (QA-21): a prontidão é lida uma vez aqui — alimenta a faixa do
 * próximo passo e os botões de baixar, que esperam quando a tela já sabe que o
 * laudo não sai (`bloqueioDoDownload`).
 */
import { useCallback, useEffect, useState } from 'react';

import { CaseStageStepper, type EvidenciasEletronicas } from '@/components/Casos/CaseStageStepper';
import { AcoesDoLaudo, type FormatoDoLaudo } from '@/components/LaudoEletronico/AcoesDoLaudo';
import { bloqueioDoDownload } from '@/components/LaudoEletronico/bloqueioDoDownload';
import { LaudoPdfPreview, type ViewerStatus } from '@/components/LaudoPdfPreview';
import { ProximoPassoDoResultado } from '@/components/ProximoPasso/ProximoPasso';
import { Blueprint } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { CodigoDoArquivoBaixado } from '@/components/CodigoDoArquivoBaixado';
import { useApiClient } from '@/lib/api';
import type { CaseRecordData } from '@/lib/cases';
import { listExamesEletronicos } from '@/lib/exame_eletronico';
import { laudoDownloadBasename, triggerDocxDownload, triggerPdfDownload } from '@/lib/laudo';
import {
  getLaudoEletronicoDocx,
  getLaudoEletronicoPdf,
  getLaudoEletronicoPreview,
  LaudoEletronicoError,
} from '@/lib/laudo_eletronico';
import { useResultadoDoCaso } from '@/lib/useResultadoDoCaso';

import { textoDaRecusa } from './textoDaRecusaDoLaudo';

const t = ptBR.laudoEletronico;

function mensagem(err: unknown): string {
  if (err instanceof LaudoEletronicoError) {
    return err.reason === 'network' ? t.errorNetwork : textoDaRecusa(err);
  }
  return t.errorGeneric;
}

export interface LaudoEletronicoPageProps {
  caso: CaseRecordData;
}

export function LaudoEletronicoPage({ caso }: LaudoEletronicoPageProps): JSX.Element {
  const client = useApiClient();
  const { resultado } = useResultadoDoCaso(client, caso.id);
  const [preview, setPreview] = useState<ViewerStatus>('loading');
  const [baixando, setBaixando] = useState<FormatoDoLaudo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // E85.F2: o código de verificação do último arquivo baixado nesta tela.
  const [codigoDoArquivo, setCodigoDoArquivo] = useState<string | null>(null);
  // E91.F1 (RF02): a esteira lê a evidência das peças e da conferência facial.
  // Falha na leitura não derruba a tela do laudo: a esteira fica no que o
  // status do caso sustenta sozinho (mesa e preliminar vencidas).
  const [evidencias, setEvidencias] = useState<EvidenciasEletronicas>({ pecas: [] });

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const raia = await listExamesEletronicos(client, caso.id);
        if (cancelado) return;
        setEvidencias({ pecas: raia.pecas });
      } catch {
        // A esteira segue pelo status do caso.
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [client, caso.id]);

  const baixar = useCallback(
    (formato: FormatoDoLaudo) => {
      setBaixando(formato);
      setErro(null);
      void (async () => {
        try {
          const basename = laudoDownloadBasename(caso);
          const baixado =
            formato === 'pdf'
              ? await getLaudoEletronicoPdf(client, caso.id)
              : await getLaudoEletronicoDocx(client, caso.id);
          if (formato === 'pdf') {
            triggerPdfDownload(baixado.blob, basename);
          } else {
            triggerDocxDownload(baixado.blob, basename);
          }
          setCodigoDoArquivo(baixado.codigoVerificacao);
        } catch (err) {
          setErro(mensagem(err));
        } finally {
          setBaixando(null);
        }
      })();
    },
    [caso, client],
  );

  return (
    <section
      className="mx-auto max-w-5xl"
      data-testid="laudo-eletronico-page"
      data-modalidade="eletronica"
      aria-labelledby="laudo-eletronico-title"
    >
      <h1 id="laudo-eletronico-title" className="text-display-md text-fg">
        {t.pageTitle}
      </h1>
      <p className="mt-1 max-w-prose text-body-md text-fg-muted">{t.pageSubtitle}</p>
      <ProximoPassoDoResultado caseId={caso.id} resultado={resultado} />

      <div className="mt-sc-4">
        <CaseStageStepper
          caseId={caso.id}
          workflowStatus={caso.workflow_status ?? null}
          activeStage="laudo"
          modalidade="eletronica"
          evidencias={evidencias}
        />
      </div>

      <AcoesDoLaudo
        caseId={caso.id}
        baixando={baixando}
        bloqueio={bloqueioDoDownload(resultado, preview)}
        onBaixar={baixar}
      />

      {erro !== null ? (
        <p
          className="mt-sc-4 border border-danger bg-danger-50 px-sc-3 py-sc-2 text-body-sm text-danger-700"
          role="alert"
          data-testid="laudo-eletronico-error"
        >
          {erro}
        </p>
      ) : null}
      <CodigoDoArquivoBaixado codigo={codigoDoArquivo} />

      <Blueprint className="mt-sc-6 p-sc-4">
        <LaudoPdfPreview
          caseId={caso.id}
          fetchPdf={getLaudoEletronicoPreview}
          onStatus={setPreview}
        />
      </Blueprint>
    </section>
  );
}
